import { useCallback, useMemo } from 'react'

import { Autocomplete, Box, Grid, InputLabel, TextField, Typography } from '@mui/material'
import { IconTrash } from '@tabler/icons-react'
import { useGetList, useTranslate } from 'react-admin'
import { ArtworkApiDTO, BeneficiaryApiDTO } from '../../codegen/generated/Api.schemas'
import { CompilationStructure } from '../../types/Compilations'
import { Product } from '../../types/Product'

type CompilationItemProps = {
  compilationStructure: CompilationStructure
  beneficiaries: BeneficiaryApiDTO[]
  onUpdate: (compilation: CompilationStructure) => void
  percent?: number
  isFirst?: boolean
  onDelete?: (compilationId: CompilationStructure['id']) => void
}

export const CompilationItem = ({
  beneficiaries,
  compilationStructure,
  percent,
  isFirst = true,
  onUpdate,
  onDelete,
}: CompilationItemProps) => {
  const translate = useTranslate()

  const _percent = useMemo(
    () => percent && (Number.isInteger(percent) ? percent : percent.toFixed(1)),
    [percent],
  )

  const productsQuery = useGetList<Product>(
    'products',
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'name', order: 'ASC' },
      filter: {
        beneficiaryId: compilationStructure.beneficiary?.id,
      },
    },
    {
      enabled: !!compilationStructure.beneficiary,
    },
  )
  const artworksQuery = useGetList<ArtworkApiDTO>(
    'artworks',
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'name', order: 'ASC' },
      filter: { productId: compilationStructure.product?.id },
    },
    {
      enabled: !!compilationStructure.product,
    },
  )
  const defaultProduct = useMemo(
    () => (productsQuery.data ?? []).find(p => p.id === compilationStructure.artwork?.productId),
    [productsQuery.data, compilationStructure.artwork?.productId],
  )

  const defaultBeneficiary = useMemo(
    () => (beneficiaries ?? []).find(b => b.id === defaultProduct?.beneficiaryId),
    [beneficiaries, defaultProduct?.beneficiaryId],
  )

  const onChangeProduct = useCallback(
    (product: Product | undefined) => {
      onUpdate({
        ...compilationStructure,
        product,
        artwork: undefined,
      })
    },
    [compilationStructure],
  )
  const onChangeArtwork = useCallback(
    (artwork: ArtworkApiDTO | undefined) => {
      onUpdate({
        ...compilationStructure,
        artwork,
      })
    },
    [compilationStructure],
  )

  return (
    <>
      {beneficiaries.length > 0 ? (
        <Grid container item direction="row" spacing={4}>
          <Grid item xs={3} gap={4}>
            {isFirst && (
              <InputLabel id="beneficiaries-compilation" style={{ fontSize: '80%' }}>
                {translate('_generics.beneficiary')}
              </InputLabel>
            )}
            <Autocomplete
              id="beneficiaries-compilation"
              defaultValue={defaultBeneficiary}
              value={compilationStructure.beneficiary}
              options={beneficiaries}
              getOptionLabel={option => option.name}
              renderInput={params => <TextField {...params} variant="standard" />}
              renderOption={(props, option, index) => (
                <li {...props} key={`beneficiary-${index}-${option.id}`}>
                  {option.name}
                </li>
              )}
              noOptionsText={translate('_generics.noResult')}
              onChange={(_, value) => {
                onUpdate({
                  id: compilationStructure.id,
                  beneficiary: value ?? undefined,
                  product: undefined,
                  artwork: undefined,
                })
              }}
            />
          </Grid>
          <Grid item xs={3} spacing={4}>
            <Box sx={{ flex: 1 }}>
              {isFirst && (
                <InputLabel id="products-compilation" style={{ fontSize: '80%' }}>
                  {translate('_generics.product')}
                </InputLabel>
              )}
              <Autocomplete
                id="products-compilation"
                defaultValue={defaultProduct}
                value={compilationStructure.product}
                options={productsQuery.data ?? []}
                getOptionLabel={option => option.name}
                renderInput={params => <TextField {...params} variant="standard" />}
                renderOption={(props, option, index) => (
                  <li {...props} key={`product-${index}-${option.id}`}>
                    {option.name}
                  </li>
                )}
                noOptionsText={translate('_generics.noResult')}
                onChange={(_, value) => {
                  onChangeProduct(value ?? undefined)
                }}
                loading={productsQuery.isLoading}
                disabled={!compilationStructure.beneficiary}
              />
            </Box>
          </Grid>
          <Grid item xs={4} spacing={4}>
            <Box>
              {isFirst && (
                <InputLabel id="artworks-compilation" style={{ fontSize: '80%' }}>
                  {translate('_generics.artwork')}
                </InputLabel>
              )}
              <Autocomplete
                id="artworks-compilation"
                value={compilationStructure.artwork}
                options={artworksQuery.data ?? []}
                getOptionLabel={option => option.name}
                renderInput={params => <TextField {...params} variant="standard" />}
                renderOption={(props, option, index) => (
                  <li {...props} key={`artwork-${index}-${option.id}`}>
                    {option.name}
                  </li>
                )}
                placeholder={translate('pages.artworks.placeholders.selectArtworks')}
                noOptionsText={translate('_generics.noResult')}
                onChange={(_, value) => {
                  onChangeArtwork(value ?? undefined)
                }}
                loading={artworksQuery.isLoading}
                disabled={!compilationStructure.beneficiary || !compilationStructure.product}
              />
            </Box>
          </Grid>
          <Grid item xs={2} display="flex" justifyContent="flex-start" alignItems="center" gap={4}>
            {_percent && (
              <Typography color="grey" mt={isFirst ? 4 : 1} noWrap>
                {_percent} %
              </Typography>
            )}
            {!isFirst && onDelete && (
              <IconTrash
                color="red"
                size={20}
                onClick={() => onDelete(compilationStructure.id)}
                cursor="pointer"
              />
            )}
          </Grid>
        </Grid>
      ) : (
        <></>
      )}
    </>
  )
}
